import clsx from 'clsx';
import * as React from 'react';
import { usePress } from 'react-aria';

import { Logger } from '@gitbook/util-debug';

const logger = Logger('ListItem');

export interface ListItemProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * List item with less vertical padding.
     */
    compact?: boolean;
    /**
     * Show the item as currently active.
     */
    active?: boolean;

    /**
     * Show the item as currently selected.
     */
    selected?: boolean;

    /**
     * Callback when pressing the item.
     * If one is passed the component is interactive
     */
    onPress?: () => void;

    /**
     * Content of the item
     */
    children: React.ReactNode;
}

const ListItemContext = React.createContext<{ labelId: string; compact: boolean }>({
    labelId: '',
    compact: false,
});

/**
 * A single item in a list.
 */
export function ListItem(props: ListItemProps): React.ReactElement {
    const {
        className,
        active = false,
        compact = false,
        selected = false,
        onPress,
        children,
        ...rest
    } = props;
    const press = usePress({ onPress });

    const labelId = React.useId();
    const contextValue = React.useMemo(() => ({ labelId, compact }), [labelId, compact]);

    if (process.env.NODE_ENV === 'development') {
        // biome-ignore lint/correctness/useHookAtTopLevel: Dev hook
        React.useLayoutEffect(() => {
            const label = document.getElementById(labelId);
            if (!label) {
                logger.error('ListItem: Missing ListItemDescription');
            }
        }, [labelId]);
    }

    return (
        <ListItemContext.Provider value={contextValue}>
            <div
                role="listitem"
                className={clsx(
                    'group/listitem flex items-center rounded-lg',
                    compact ? 'gap-2 py-1' : 'gap-3 p-3',
                    selected ? 'bg-muted' : 'bg-base',
                    onPress
                        ? 'cursor-pointer hover:bg-list-item-hover focus-visible:shadow-focus-ring'
                        : null,
                    className
                )}
                aria-current={active || undefined}
                aria-selected={selected || undefined}
                aria-labelledby={labelId}
                {...(onPress
                    ? {
                          ...press.pressProps,
                          tabIndex: 0,
                      }
                    : {})}
                {...rest}
            >
                {children}
            </div>
        </ListItemContext.Provider>
    );
}

/**
 * Indicator/Icon to be displayed on the left side of the list item.
 */
export function ListItemIndicator(
    props: { children: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
    const { children, className, ...rest } = props;
    return (
        <div className={clsx('flex shrink-0 items-center', className)} {...rest}>
            {children}
        </div>
    );
}

/**
 * Main body of the list item.
 */
export function ListItemBody(
    props: { children: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
    const { children, className, ...rest } = props;
    const { compact } = React.useContext(ListItemContext);
    return (
        <div
            className={clsx('flex flex-1 flex-col', compact ? 'gap-0' : 'gap-1', className)}
            {...rest}
        >
            {children}
        </div>
    );
}

/**
 * Text description to be displayed as the first line in a list item's body.
 */
export function ListItemDescription(
    props: { children: React.ReactNode } & React.HTMLAttributes<HTMLParagraphElement>
): React.ReactElement {
    const { children, className, ...rest } = props;
    const { labelId } = React.useContext(ListItemContext);
    return (
        <div id={labelId} className={clsx('text-secondary text-ui-base', className)} {...rest}>
            {children}
        </div>
    );
}

/**
 * Text title to be displayed as the second line in a list item's body.
 */
export function ListItemDetail(
    props: { children: React.ReactNode } & React.HTMLAttributes<HTMLParagraphElement>
): React.ReactElement {
    const { children, className, ...rest } = props;
    return (
        <div className={clsx('text-muted text-ui-small', className)} {...rest}>
            {children}
        </div>
    );
}

/**
 * Buttons/Controls to be displayed on the right side of the list item.
 */
export function ListItemControls(
    props: {
        /**
         * When to display the controls, based on the item's state.
         *   - 'always': always show the controls
         *   - 'focused': show the controls when the item is focused/hovered
         * @default 'focused'
         */
        when?: 'always' | 'focused';

        /**
         * Buttons to be displayed on the right side of the list item.
         */
        children: React.ReactNode;
    } & React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
    const { children, when = 'focused', ...rest } = props;
    return (
        <div
            className={clsx(
                'flex',
                when === 'always'
                    ? null
                    : 'opacity-0 group-[[aria-selected]]/listitem:opacity-100 has-[:focus-visible]:opacity-100',
                when === 'focused'
                    ? 'group-hover/listitem:opacity-100 group-focus-visible/listitem:opacity-100'
                    : null
            )}
            {...rest}
        >
            {children}
        </div>
    );
}
